import { useEffect } from 'react';
import { Form } from 'react-bootstrap';
import dayjs from 'dayjs';
import { QuickDateOption } from './Reports/BaseReport';

export interface DateRange {
  from: string;
  to: string;
}

interface Props {
  mode: QuickDateOption | 'All';
  onChange: (range: DateRange, mode: QuickDateOption | 'All') => void;
  onModeChange: (mode: QuickDateOption | 'All') => void; 
  label: React.ReactNode;
  excludeAll?: boolean;
}

export function QuickDateRangeDropdown({ mode, onChange, onModeChange, label = 'Date Range', excludeAll }: Props) {
  useEffect(() => {
    if (!mode) {
      onChange({ from: 'All', to: 'All' }, 'All');
      return;
    }

    const range = getDynamicDateRange(mode);
    if (range) {
      onChange(range, mode);
    }
  }, [mode]);

  return (
    <Form.Group controlId="quickDateRange">
      <Form.Label className="small form-label">{label}</Form.Label>
      <Form.Select
        value={mode}
        onChange={(e) => onModeChange(e.target.value as QuickDateOption | 'All')}
        size="sm"
      >
        {!excludeAll && <option>All</option>}
        <option>Today</option>
        <option>Yesterday</option>
        <option>Last 7 Days</option>
        <option>Last 30 Days</option>
        <option>This Month</option>
        <option>Last Month</option>
        <option>This Year</option>
        <option>Custom</option>
      </Form.Select>
    </Form.Group>
  );
}

export function getDynamicDateRange(mode: string) {
  const today = dayjs();
  let from = today;
  let to = today;

  switch (mode) {
    case 'Today':
      from = today.startOf('day');
      to = today.endOf('day');
      break;
    case 'Yesterday':
      from = today.subtract(1, 'day').startOf('day');
      to = today.subtract(1, 'day').endOf('day');
      break;
    case 'Last 7 Days':
      from = today.subtract(6, 'day').startOf('day');
      to = today.endOf('day');
      break;
    case 'Last 30 Days':
      from = today.subtract(29, 'day').startOf('day');
      to = today.endOf('day');
      break;
    case 'This Month':
      from = today.startOf('month');
      to = today.endOf('month');
      break;
    case 'Last Month':
      from = today.subtract(1, 'month').startOf('month');
      to = today.subtract(1, 'month').endOf('month');
      break;
    case 'This Year':
      from = today.startOf('year');
      to = today.endOf('year');
      break;
    default:
      return null;
  }

  return {
    from: from.format('YYYY-MM-DD HH:mm:ss'),
    to: to.format('YYYY-MM-DD HH:mm:ss'),
  };
}
